// Formatting helpers matching the number/date conventions used throughout the
// prototype (deploy/dashboard/web/design/Trading Bot Dashboard v2.dc.html —
// see fmtMoney/fmtPnl/fmtT/fmtDur in its <script>): ru-RU grouping, a real
// minus sign (−) instead of a hyphen, and a leading $ only on absolute money
// amounts (not on P&L deltas, which get a +/− sign instead).

function toNumber(v: string | number): number {
  return typeof v === "number" ? v : parseFloat(v);
}

export function formatMoney(v: string | number, decimals = 2): string {
  const n = toNumber(v);
  if (Number.isNaN(n)) return "—";
  const s = Math.abs(n).toLocaleString("ru-RU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (n < 0 ? "−$" : "$") + s;
}

export function formatPnl(v: string | number, decimals = 2): string {
  const n = toNumber(v);
  if (Number.isNaN(n)) return "—";
  const s = Math.abs(n).toLocaleString("ru-RU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (n < 0 ? "−" : "+") + s;
}

// Same +/− sign as formatPnl, but with the $ a P&L figure needs when shown
// on its own (not next to another already-labeled money value) — user
// request: trade detail's header P&L read as a bare "+34,69" with no unit.
export function formatPnlMoney(v: string | number, decimals = 2): string {
  const n = toNumber(v);
  if (Number.isNaN(n)) return "—";
  const s = Math.abs(n).toLocaleString("ru-RU", { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
  return (n < 0 ? "−$" : "+$") + s;
}

export function formatPct(v: string | number, decimals = 2): string {
  const n = toNumber(v);
  if (Number.isNaN(n)) return "—";
  return (n < 0 ? "−" : "+") + Math.abs(n).toFixed(decimals) + "%";
}

// All timestamps from the API are ISO-8601 UTC (see API_CONTRACT.md) — every
// on-screen time in this app is displayed in Europe/Moscow, per requirement.
// Intl.DateTimeFormat (not a hardcoded +3 offset) so DST-less MSK stays
// correct without relying on that being a permanent fact about the zone.
const MSK_TZ = "Europe/Moscow";

// en-GB + explicit 2-digit fields gives predictable DD/MM/HH/MM part values
// regardless of locale ordering quirks — parsed back out below, not used as
// the literal rendered string.
function mskParts(d: Date): Record<string, string> {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: MSK_TZ,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).formatToParts(d);
  return Object.fromEntries(parts.map((p) => [p.type, p.value]));
}

export function formatTime(iso: string | null | undefined, withDate = false): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = mskParts(d);
  const time = `${p.hour}:${p.minute}`;
  if (!withDate) return time;
  return `${p.day}.${p.month} ${time}`;
}

// Date-only, MSK ("DD.MM") — used for the price chart's own axis tick
// labels at day/month tick marks (see PriceChart.tsx's tickMarkFormatter).
export function formatMskDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = mskParts(d);
  return `${p.day}.${p.month}`;
}

// Full date, MSK ("DD.MM.YYYY") — unlike formatMskDate (year-less, fine for
// chart ticks within one season) this is for long-lived records like a
// user's registration date, where the year is the whole point.
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const p = mskParts(d);
  return `${p.day}.${p.month}.${p.year}`;
}

// Live header clock (AppShell) — includes seconds, MSK.
export function formatClockMsk(d: Date): string {
  const p = mskParts(d);
  return `${p.hour}:${p.minute}:${p.second}`;
}

export function formatDuration(startIso: string, endIso?: string): string {
  const a = new Date(startIso).getTime();
  const b = endIso ? new Date(endIso).getTime() : Date.now();
  const m = Math.round((b - a) / 60000);
  const h = Math.floor(m / 60);
  return h ? `${h}ч ${m % 60}м` : `${m}м`;
}

// trade_id is "{bot_instance_id}_{YYYYMMDD}_{HHMMSS}_{LONG|SHORT}" (see
// trade_bot/logging/db_logger.py) — e.g. "daily-01_20260815_075220_LONG".
// Full precision, but too long for a table column or an inline label. Short
// label keeps just the bot-instance number + open time (HH:MM, MSK) since
// direction/date are already shown elsewhere next to it (a colored
// LONG/SHORT badge, a separate "Открытие" column) — enough to tell trades
// apart without repeating what's already on screen. Falls back to the raw
// ID if the format ever changes.
const TRADE_ID_RE = /^([a-z]+)-(\d+)_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})_(LONG|SHORT)$/i;

export function formatTradeId(tradeId: string): string {
  const m = TRADE_ID_RE.exec(tradeId);
  if (!m) return tradeId;
  const [, , instanceNum, , , , hh, mm] = m;
  // HH:MM here is UTC (the timestamp embedded in trade_id, same as the bot's
  // host clock) — deliberately not MSK-shifted, since it's just a distinct
  // stub next to the already-MSK "Открытие" time and doesn't claim to be a
  // display time itself. Actual open/close times are always shown separately
  // in MSK via formatTime().
  return `#${instanceNum}·${hh}${mm}`;
}

export function formatAgo(iso: string): string {
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.max(0, Math.round(diffMs / 1000));
  if (s < 60) return `${s}с`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}м`;
  const h = Math.round(m / 60);
  return `${h}ч`;
}
